/*
  The vendor-status half of the SendGrid screen.

  SendGrid has no health service of its own — there is no `sendgrid/overview` and
  no `sendgrid/incidents` — so its uptime story is `sendgrid/status`: the
  vendor's public status page, plus a probe of this deployment's own SendGrid
  credentials. Both are translated here.

  Its *delivery* figures are not here and are not a special case any more: they
  come from `sendgrid/messaging/*`, the same four routes Twilio has, and are
  rendered by the shared adapters in `messaging-data.ts`.
*/

import type {
  VendorAccountProbe,
  VendorStatusPayload,
} from './api/deep-dive.types'

/* ------------------------------------------------------------ vendor status */

/** How a Statuspage severity reads on a tile. */
export interface VendorStatusView {
  /**
   * One word for the headline slot — "Operational", "Degraded".
   *
   * Kept apart from `detail` because the shared `StatTile` renders its value at
   * 4xl mono, which is a size for a value and not for a sentence. This screen
   * used to put the vendor's whole description there and shrink the type to make
   * it fit, which is most of why it looked unlike every other page.
   */
  word: string
  /** The vendor's own wording, e.g. "Partially Degraded Service". */
  detail: string
  /** The four tones `StatTile` knows; maintenance folds into neutral. */
  tone: 'healthy' | 'degraded' | 'critical' | 'neutral'
  /** True for anything other than an all-clear — the banner shows on these. */
  degraded: boolean
}

/**
 * Statuspage severity → how the tile reads.
 *
 * The vendor's own `description` is preferred over anything invented here: it is
 * the sentence the vendor is showing the rest of the world, and an on-call
 * engineer comparing this screen against the vendor's status page should see the
 * same words on both.
 */
export function toVendorStatus(payload: VendorStatusPayload | undefined): VendorStatusView {
  const severity = (payload?.indicator ?? payload?.status ?? '').toLowerCase()
  const detail = payload?.description?.trim() || fallbackLabel(severity)

  switch (severity) {
    case 'none':
    case 'operational':
      return { word: 'Operational', detail, tone: 'healthy', degraded: false }
    case 'minor':
      return { word: 'Degraded', detail, tone: 'degraded', degraded: true }
    case 'major':
      return { word: 'Major outage', detail, tone: 'critical', degraded: true }
    case 'critical':
      return { word: 'Critical', detail, tone: 'critical', degraded: true }
    case 'maintenance':
      return { word: 'Maintenance', detail, tone: 'neutral', degraded: false }
    default:
      // An unrecognised severity is not an all-clear. Say so, rather than
      // painting it green because it failed to match a known bad value.
      return { word: 'Unknown', detail, tone: 'neutral', degraded: false }
  }
}

function fallbackLabel(severity: string): string {
  if (!severity) return 'Status unknown'
  return severity.charAt(0).toUpperCase() + severity.slice(1)
}

/* ------------------------------------------------------------ account probe */

export interface AccountProbeView {
  /** Headline: the HTTP status our credentials got, or why none was tried. */
  value: string
  tone: 'healthy' | 'degraded' | 'critical' | 'neutral'
  /** One line under it — today's account totals, or what is missing. */
  caption: string
  /** The URL actually called, shown as the probe's receipt. */
  endpoint?: string
  missingEnv?: string[]
  checkedAt?: string
}

/**
 * The account probe — the half of this payload that is genuinely ours.
 *
 * Unconfigured is amber rather than red on purpose: nothing is broken, the
 * deployment simply holds no credentials for this vendor, and colouring it as an
 * outage would send someone hunting a failure that has not happened. Twilio
 * reports exactly that state today (`missing_env: TWILIO_ACCOUNT_SID, …`).
 */
export function toAccountProbe(probe: VendorAccountProbe | undefined): AccountProbeView | null {
  if (!probe) return null

  if (!probe.configured) {
    return {
      value: 'Not configured',
      tone: 'degraded',
      caption: probe.detail?.trim() || 'No credentials are set for this vendor.',
      missingEnv: probe.missing_env,
      checkedAt: probe.checked_at,
    }
  }

  const http = probe.http_status
  const ok = typeof http === 'number' && http >= 200 && http < 300

  return {
    value: typeof http === 'number' ? `HTTP ${http}` : 'Reachable',
    tone: ok ? 'healthy' : 'critical',
    // The detail carries the vendor's own account summary for today — the one
    // figure here that covers the whole account rather than just the battery
    // calculator's messages.
    caption:
      probe.detail?.trim() || (ok ? 'Credentials accepted.' : 'The vendor rejected the call.'),
    endpoint: probe.endpoint,
    checkedAt: probe.checked_at,
  }
}
